import type { TFunction } from "i18next"
import React, { createContext, useContext, useState } from "react"

import { useAppContext } from "@contexts/app_context"
import { getUsers } from "@domain/users"
import useToggle from "@hooks/use_toggle"
import i18next from "@utils/localization"
// import { showErrorToast } from "@utils/toast"
import { useQuery } from "@tanstack/react-query"
import { User } from "__generated__/graphql"

type UserPageContextProps = {
  translate: TFunction<"translation", undefined>
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  usersData?: User[]
  setUsersData?: React.Dispatch<React.SetStateAction<User[]>>
  search?: string
  setSearch: (search: string) => void
  isLoading: boolean
  pagination?: {
    page: number
    pageSize: number
  }
  setPagination?: React.Dispatch<
    React.SetStateAction<{
      page: number
      pageSize: number
    }>
  >
}

const defaultValues: UserPageContextProps = {
  translate: i18next.t,
  isLoading: true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSearch: (search: string) => {}
}

const UserPageContext = createContext(defaultValues)

export function UserPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useAppContext()
  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20
  })
  const [search, setSearch] = useState("")

  const { data: usersData, isLoading } = useQuery({
    queryKey: ["users-data-table", pagination, search],
    queryFn: async () => {
      const response = await getUsers()
      return response.data?.data as User[]
    }
  })

  return (
    <UserPageContext.Provider
      value={{
        ...defaultValues,
        translate,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        usersData,
        search,
        setSearch,
        pagination,
        setPagination,
        isLoading
      }}
    >
      {props.children}
    </UserPageContext.Provider>
  )
}

export function useUserPageContext() {
  const context = useContext(UserPageContext)
  if (context === undefined) {
    throw new Error("UserPage Context Error")
  }
  return context
}
